import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import QLogo from "@/components/QLogo";
import ShareButton from "@/components/ShareButton";
import { ArrowRight, Lightning, CurrencyInr, Bell, CheckCircle } from "@phosphor-icons/react";

const HERO_IMG = "/hero-bg.png";

const TRUST = [
  { icon: Lightning, label: "Fast Closure in 60 Minutes" },
  { icon: CurrencyInr, label: "Immediate Settlement" },
];

export default function Home() {
  const nav = useNavigate();
  const [notifCount, setNotifCount] = useState(0);
  const [acquisitions, setAcquisitions] = useState([]);

  useEffect(() => {
    api.get("/notifications").then(r => setNotifCount((r.data || []).filter(x => !x.seen).length)).catch((err) => console.error("notifications fetch failed:", err));
    api.get("/acquisitions/recent?limit=6").then(r => setAcquisitions(r.data || [])).catch((err) => console.error("acquisitions fetch failed:", err));
  }, []);

  return (
    <div className="min-h-screen pb-32" data-testid="home-page">
      <section className="relative min-h-screen overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-55" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/72 to-black" />
        </div>

        <div className="relative px-6 pb-5 qd-pt-safe-header flex items-center justify-between">
          <QLogo size={28} />
          <div className="flex items-center gap-2">
            <ShareButton testid="home-share" title="Q Drives" text="Sell your car directly to Q Drives — verified buyers, instant settlement." />
            <button
              data-testid="notifications-bell"
              onClick={() => nav("/activity")}
              className="relative qd-glass rounded-full p-2.5 hover:bg-white/[0.06] transition-colors"
            >
              <Bell size={16} className="text-white/85" />
              {notifCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FF1E1E] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {notifCount > 9 ? "9+" : notifCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="relative max-w-5xl mx-auto px-6 pt-14 sm:pt-24">
          <div className="qd-overline mb-5">Hyderabad</div>
          <h1 className="qd-h1 text-[42px] sm:text-6xl lg:text-7xl leading-[0.95] text-white">
            Sell your car.<br />
            <span className="text-[#FF1E1E]">Get paid instantly.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/65 max-w-md leading-relaxed">
            Direct acquisition by verified buyers.
          </p>

          <div className="mt-10 max-w-md space-y-3">
            <button
              data-testid="cta-sell-car"
              onClick={() => nav("/sell")}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              Sell My Car <ArrowRight size={15} weight="bold" />
            </button>
            <button
              data-testid="cta-buy-car"
              onClick={() => nav("/buy")}
              className="btn-secondary w-full"
            >
              Find a Car
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mt-10">
            {TRUST.map(({ icon: Icon, label }, i) => (
              <span key={i} className="qd-pill" data-testid={`trust-${i}`}>
                <Icon size={13} className="text-[#FF1E1E]" weight="fill" /> {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {acquisitions.length > 0 && (
        <section className="relative px-6 max-w-5xl mx-auto -mt-6 pb-6" data-testid="recent-acquisitions">
          <div className="qd-overline mb-4">Recently Acquired</div>
          <div className="space-y-2">
            {acquisitions.map((a) => (
              <div
                key={a.id}
                data-testid={`acquisition-${a.id}`}
                className="qd-card flex items-stretch gap-3 overflow-hidden"
              >
                {a.image_url ? (
                  <div className="w-24 sm:w-32 shrink-0 bg-[#0E0E0E] overflow-hidden">
                    <img
                      src={a.image_url}
                      alt={`${a.make} ${a.model}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                ) : (
                  <div className="w-1 shrink-0 bg-[#C20505]" />
                )}
                <div className="flex-1 min-w-0 py-3 pr-4 flex items-center gap-3">
                  {!a.image_url && <CheckCircle size={16} weight="fill" className="text-[#C20505] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-[13px] text-white truncate">
                      {a.year ? `${a.year} ` : ""}{a.make} {a.model}{a.variant ? ` ${a.variant}` : ""}
                    </div>
                    <div className="text-[11px] text-white/50 truncate">
                      {a.location && <>{a.location} · </>}{a.acquired_on || "Recently"}
                      {a.note && <> · {a.note}</>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-white/40 mt-3">Curated by the Q Drives acquisition team.</div>
        </section>
      )}

      {/* Public site footer — Legal + copyright. Rendered on Home so it's
          visible on the primary landing without altering the app shell. */}
      <footer
        data-testid="site-footer"
        className="mt-16 pt-6 border-t border-[#1A1A1A] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[10px] font-mono-data tracking-widest uppercase text-white/45"
      >
        <div>© Q Drives Automotive Private Limited</div>
        <nav className="flex items-center gap-4">
          <Link
            to="/privacy-policy"
            data-testid="site-footer-privacy-link"
            className="text-white/60 hover:text-white underline decoration-white/20 hover:decoration-white"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            data-testid="site-footer-terms-link"
            className="text-white/60 hover:text-white underline decoration-white/20 hover:decoration-white"
          >
            Terms
          </Link>
        </nav>
      </footer>
    </div>
  );
}
